import { useState, useEffect } from 'react';
import { useAppTheme } from '@/context/ThemeContext';
import { createServerSupabaseClient, getCurrentUserFromSSR, hasBusinessOrAdminPermission } from '@/services/supabase';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card_Widget';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, PieChart, LineChart } from '@/components/charts';
import { MagnifyingGlassIcon, ChartBarIcon, ShoppingBagIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import ExportButtons from '@/components/ExportButtons';
import type { BusinessAnalytics } from '@/utils/export';

// Constants for data visualization
const BERMUDA_PARISHES = [
  'Hamilton', 'Pembroke', 'Devonshire', 'Smith\'s', 
  'Southampton', 'Warwick', 'Sandys', 'St. George\'s'
];

const TIME_SLOTS = [
  '6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM',
  '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM',
  '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM'
];

const DIETARY_PREFERENCES = [
  'Vegetarian', 'Vegan', 'Gluten-Free', 'Halal', 'Kosher',
  'Dairy-Free', 'Nut-Free', 'No Restrictions'
];

// Helper functions to generate mock data for SSR
const generateMockRevenueData = () => {
  return {
    monthly: Array.from({ length: 12 }, (_, i) => ({
      month: new Date(2023, i, 1).toLocaleString('default', { month: 'short' }),
      revenue: Math.floor(Math.random() * 10000) + 5000,
    })),
    weekly: Array.from({ length: 7 }, (_, i) => ({
      day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
      revenue: Math.floor(Math.random() * 2000) + 1000,
    })),
    yearly: Array.from({ length: 5 }, (_, i) => ({
      year: 2019 + i,
      revenue: Math.floor(Math.random() * 100000) + 50000,
    })),
  };
};

const generateMockOrderData = () => {
  return {
    total: Math.floor(Math.random() * 1000) + 500,
    completed: Math.floor(Math.random() * 800) + 400,
    pending: Math.floor(Math.random() * 200) + 50,
    cancelled: Math.floor(Math.random() * 50) + 10,
    byStatus: [
      { status: 'Completed', value: 65 },
      { status: 'Processing', value: 15 },
      { status: 'Pending', value: 10 },
      { status: 'Cancelled', value: 10 },
    ],
  };
};

const generateMockProductData = () => {
  return {
    topProducts: [
      { name: 'Fresh Salmon', sales: 120, revenue: 3600 },
      { name: 'Cod Fillets', sales: 95, revenue: 2850 },
      { name: 'Tuna Steaks', sales: 85, revenue: 2550 },
      { name: 'Shrimp', sales: 75, revenue: 2250 },
      { name: 'Lobster', sales: 65, revenue: 3900 },
    ],
    byCategory: [
      { category: 'Fish', value: 45 },
      { category: 'Shellfish', value: 30 },
      { category: 'Prepared', value: 15 },
      { category: 'Other', value: 10 },
    ],
  };
};

const generateMockCustomerData = () => {
  return {
    total: Math.floor(Math.random() * 500) + 200,
    new: Math.floor(Math.random() * 50) + 20,
    returning: Math.floor(Math.random() * 450) + 150,
    byType: [
      { type: 'Restaurants', value: 40 },
      { type: 'Retailers', value: 25 },
      { type: 'Individuals', value: 20 },
      { type: 'Other', value: 15 },
    ],
  };
};

/**
 * Server-side function to fetch analytics data for SSR
 * Uses centralized SSR methods from supabase.ts
 */
export async function getAnalyticsDashboardDataSSR(context: any) {
  try {
    // Create a Supabase client for server-side operations
    const supabaseClient = createServerSupabaseClient(context);
    
    // Get the user using the SSR method
    const user = await getCurrentUserFromSSR(supabaseClient);
    
    // Check if user has proper permissions using the centralized function
    if (!hasBusinessOrAdminPermission(user)) {
      return { user: null, data: null };
    }
    
    // In a real implementation, fetch analytics data from API
    // For now, we'll return mock data
    const mockData = {
      revenueData: generateMockRevenueData(),
      orderData: generateMockOrderData(),
      productData: generateMockProductData(),
      customerData: generateMockCustomerData()
    };
    
    return { user, data: mockData };
  } catch (error) {
    console.error('Error in getAnalyticsDashboardDataSSR:', error);
    return { user: null, data: null };
  }
}

export default function AnalyticsDashboard({ initialData }: { initialUser?: any, initialData?: any }) {
  const { themeConfig } = useAppTheme();
  const [loading, setLoading] = useState(!initialData);
  const [filterPeriod, setFilterPeriod] = useState('month'); // 'week', 'month', 'year'
  const [inventoryFilter, setInventoryFilter] = useState('all'); // 'all', 'low', 'expiring'
  const [customerSegment, setCustomerSegment] = useState('all'); // 'all', 'new', 'returning', 'inactive'
  
  // Using stats state with complete data
  const [baseStats] = useState({
    // Base data that will be filtered
    totalRevenue: 45678.90,
    totalOrders: 1234,
    activeProducts: 567,
    activeCustomers: 890,
    foodInventory: {
      total: 2500,
      sold: 1800,
      unsold: 450,
      donated: 250,
      thisMonth: {
        sold: 450,
        unsold: 120,
        donated: 80
      },
      lastMonth: {
        sold: 420,
        unsold: 100,
        donated: 70
      },
      thisYear: {
        sold: 1800,
        unsold: 450,
        donated: 250
      }
    },
    topSellingProducts: {
      'Product A': 1200,
      'Product B': 980,
      'Product C': 850,
      'Product D': 720,
      'Product E': 650
    },
    productCategories: {
      'Category A': 35,
      'Category B': 25,
      'Category C': 20,
      'Category D': 15,
      'Category E': 5
    },
    customerDemographics: {
      parishDistribution: BERMUDA_PARISHES.reduce((acc, parish) => ({
        ...acc,
        [parish]: Math.floor(Math.random() * 200) + 50
      }), {}),
      orderTimeDistribution: TIME_SLOTS.reduce((acc, time) => ({
        ...acc,
        [time]: Math.floor(Math.random() * 100) + 20
      }), {}),
      dietaryPreferences: DIETARY_PREFERENCES.reduce((acc, pref) => ({
        ...acc,
        [pref]: Math.floor(Math.random() * 150) + 30
      }), {})
    },
    // Period-specific data
    week: {
      salesData: [
        { name: 'Mon', value: 1200 },
        { name: 'Tue', value: 1400 },
        { name: 'Wed', value: 1100 },
        { name: 'Thu', value: 1600 },
        { name: 'Fri', value: 1800 },
        { name: 'Sat', value: 2000 },
        { name: 'Sun', value: 1500 },
      ],
      orderStatus: [
        { name: 'Completed', value: 70 },
        { name: 'Processing', value: 15 },
        { name: 'Pending', value: 10 },
        { name: 'Cancelled', value: 5 },
      ],
    },
    month: {
      salesData: [
        { name: 'Jan', value: 2400 },
        { name: 'Feb', value: 1398 },
        { name: 'Mar', value: 9800 },
        { name: 'Apr', value: 3908 },
        { name: 'May', value: 4800 },
        { name: 'Jun', value: 3800 },
      ],
      orderStatus: [
        { name: 'Completed', value: 65 },
        { name: 'Processing', value: 20 },
        { name: 'Pending', value: 10 },
        { name: 'Cancelled', value: 5 },
      ],
    },
    year: {
      salesData: [
        { name: '2022', value: 35000 },
        { name: '2023', value: 42000 },
        { name: '2024', value: 48000 },
        { name: '2025', value: 45678 },
      ],
      orderStatus: [
        { name: 'Completed', value: 75 },
        { name: 'Processing', value: 10 },
        { name: 'Pending', value: 8 },
        { name: 'Cancelled', value: 7 },
      ],
    },
    // Inventory-specific data
    all: {
      topProducts: {
        'Product A': 1200,
        'Product B': 980,
        'Product C': 850,
        'Product D': 720,
        'Product E': 650
      },
      categories: {
        'Category A': 35,
        'Category B': 25,
        'Category C': 20,
        'Category D': 15,
        'Category E': 5
      }
    },
    low: {
      topProducts: {
        'Product B': 980,
        'Product E': 650,
        'Product F': 450,
      },
      categories: {
        'Category A': 15,
        'Category C': 10,
        'Category E': 5
      }
    },
    expiring: {
      topProducts: {
        'Product A': 800,
        'Product C': 600,
        'Product G': 550,
      },
      categories: {
        'Category B': 20,
        'Category D': 15,
        'Category F': 8
      }
    },
    // Customer-specific data
    newCustomers: {
      distribution: {
        'Hamilton': 85,
        'Pembroke': 65,
        'Devonshire': 45,
        'Smith\'s': 35,
      },
      timeDistribution: {
        '8:00 AM': 10,
        '12:00 PM': 25,
        '4:00 PM': 45,
        '7:00 PM': 20,
      }
    },
    returningCustomers: {
      distribution: {
        'Hamilton': 120,
        'Pembroke': 95,
        'Southampton': 75,
        'Warwick': 65,
        'St. George\'s': 55,
      },
      timeDistribution: {
        '7:00 AM': 15,
        '9:00 AM': 35,
        '1:00 PM': 55,
        '5:00 PM': 70,
        '8:00 PM': 25,
      }
    },
    inactiveCustomers: {
      distribution: {
        'Sandys': 45,
        'Warwick': 35,
        'St. George\'s': 25,
        'Smith\'s': 20,
      },
      timeDistribution: {
        '10:00 AM': 15,
        '2:00 PM': 20,
        '6:00 PM': 30,
      }
    }
  });
  
  // Define types for our stats
  type ChartDataPoint = { name: string; value: number };
  
  interface BusinessStats {
    totalRevenue: number;
    totalOrders: number;
    activeProducts: number;
    activeCustomers: number;
    foodInventory: any;
    topSellingProducts: Record<string, number>;
    productCategories: Record<string, number>;
    customerDemographics: any;
    salesData?: ChartDataPoint[];
    orderStatus?: ChartDataPoint[];
    customerData?: any;
    productData?: any;
  }
  
  // Stats state that will be filtered based on selections
  const [stats, setStats] = useState<BusinessStats>({
    totalRevenue: 45678.90,
    totalOrders: 1234,
    activeProducts: 567,
    activeCustomers: 890,
    foodInventory: {
      total: 2500,
      sold: 1800,
      unsold: 450,
      donated: 250,
      thisMonth: {
        sold: 450,
        unsold: 120,
        donated: 80
      },
      lastMonth: {
        sold: 420,
        unsold: 100,
        donated: 70
      },
      thisYear: {
        sold: 1800,
        unsold: 450,
        donated: 250
      }
    },
    topSellingProducts: {
      'Product A': 1200,
      'Product B': 980,
      'Product C': 850,
      'Product D': 720,
      'Product E': 650
    },
    productCategories: {
      'Category A': 35,
      'Category B': 25,
      'Category C': 20,
      'Category D': 15,
      'Category E': 5
    },
    customerDemographics: {
      parishDistribution: BERMUDA_PARISHES.reduce((acc, parish) => ({
        ...acc,
        [parish]: Math.floor(Math.random() * 200) + 50
      }), {}),
      orderTimeDistribution: TIME_SLOTS.reduce((acc, time) => ({
        ...acc,
        [time]: Math.floor(Math.random() * 100) + 20
      }), {}),
      dietaryPreferences: DIETARY_PREFERENCES.reduce((acc, pref) => ({
        ...acc,
        [pref]: Math.floor(Math.random() * 150) + 30
      }), {})
    }
  });

  // For filtering search terms
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter data based on search term
  const filterData = (obj: Record<string, number>, term: string): Record<string, number> => {
    if (!term) return obj;
    
    // For objects with numeric values
    if (typeof obj === 'object' && !Array.isArray(obj)) {
      const filtered: Record<string, number> = {};
      for (const [key, value] of Object.entries(obj)) {
        if (key.toLowerCase().includes(term.toLowerCase())) {
          filtered[key] = value;
        }
      }
      return filtered;
    }
    return obj;
  };
  
  
  // Get filtered product data based on inventory filter
  const getFilteredProductData = (filter: string) => {
    const inventoryData = filter === 'low' ? baseStats.low : 
                         filter === 'expiring' ? baseStats.expiring : baseStats.all;
    return {
      topProducts: filterData(inventoryData.topProducts, searchTerm),
      categories: filterData(inventoryData.categories, searchTerm)
    };
  };
  
  // Get filtered customer data based on customer segment
  const getFilteredCustomerData = (segment: string) => {
    if (segment === 'new') {
      return baseStats.newCustomers;
    } else if (segment === 'returning') {
      return baseStats.returningCustomers;
    } else if (segment === 'inactive') {
      return baseStats.inactiveCustomers;
    } else {
      // All customers - combine data
      return {
        distribution: baseStats.customerDemographics.parishDistribution,
        timeDistribution: baseStats.customerDemographics.orderTimeDistribution
      };
    }
  };
  
  const filteredProductData = getFilteredProductData(inventoryFilter);
  const filteredCustomerData = getFilteredCustomerData(customerSegment);
  
  // Filter the top selling products and product categories
  const filteredTopSellingProducts = filteredProductData.topProducts;
  const filteredProductCategories = filteredProductData.categories;
  
  // Handle data loading with SSR fallback to client-side
  useEffect(() => {
    // If we have initialData from SSR, use it and skip client-side fetching
    if (initialData) {
      setLoading(false);
      return;
    }
    
    // Otherwise, fetch data on the client side
    const fetchData = async () => {
      try {
        // In a real implementation, fetch analytics data from API
        // For now, we'll simulate a delay and use mock data
        setTimeout(() => {
          setLoading(false);
        }, 1500);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [initialData]);

  // Fetch data based on filters
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get current user directly from Supabase
        const { createClient, normalizeUser } = await import('@/services/supabase');
        const supabase = createClient();
        const { data: { session } } = await (await supabase).auth.getSession();
        
        // Check if we have a valid session
        if (!session?.user) {
          console.error('No valid Supabase session found');
          setLoading(false);
          return;
        }
        
        // Use normalizeUser to get consistent user data
        const normalizedUser = normalizeUser(session.user);
        const userRole = normalizedUser.role;
        
        // Check if user has proper permissions
        const isBusiness = userRole === 'business';
        const isSuperAdmin = userRole === 'super_admin';
        
        if (!isBusiness && !isSuperAdmin) {
          console.error('User does not have business or super_admin role');
          setLoading(false);
          return;
        }
        
        // In a real application, you would call your API with the appropriate filters and businessId
        // const response = await business.statistics.getAnalytics(businessId, filterPeriod, inventoryFilter, customerSegment);
        // setStats(response);
        
        // Prepare the period-specific data
        const periodData = filterPeriod === 'week' ? baseStats.week : 
                          filterPeriod === 'year' ? baseStats.year : baseStats.month;
        
        // Update the stats based on selected filters
        setStats({
          // Keep base stats
          ...baseStats,
          // Update filtered data
          totalRevenue: baseStats.totalRevenue * (filterPeriod === 'week' ? 0.25 : filterPeriod === 'year' ? 4 : 1),
          totalOrders: baseStats.totalOrders * (filterPeriod === 'week' ? 0.2 : filterPeriod === 'year' ? 3.5 : 1),
          // Use period-specific chart data
          salesData: periodData.salesData,
          orderStatus: periodData.orderStatus,
          // Use segment-specific data
          customerData: getFilteredCustomerData(customerSegment),
          // Use inventory-specific data
          productData: getFilteredProductData(inventoryFilter)
        });
        
        // For demo purposes, we'll just simulate a delay
        setTimeout(() => {
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error('Error fetching analytics:', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [filterPeriod, inventoryFilter, customerSegment]);

  // Cast baseStats to BusinessAnalytics type for export
  const businessAnalyticsData: BusinessAnalytics = baseStats;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-xl font-medium text-navy-blue">Analytics Dashboard</h1>
            </div>
            
            {/* Export Buttons */}
            <ExportButtons
              data={businessAnalyticsData}
              title="Business Analytics Report"
              subtitle="Performance Overview"
              type="analytics"
              filename="business-analytics-report"
            />
          </div>
        </div>
        <div className="space-y-6">
          <div className="flex items-center">
            
          </div>
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="products">Products</TabsTrigger>
              <TabsTrigger value="customers">Customers</TabsTrigger>
            </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
              <div className="relative w-full sm:w-64">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:border-sky-blue focus:ring-1 focus:ring-sky-blue"
                  placeholder="Search analytics..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="inline-flex rounded-md shadow-sm">
                <button
                  onClick={() => setFilterPeriod('week')}
                  className={`px-4 py-2 text-sm font-medium rounded-l-md ${filterPeriod === 'week' ? 'bg-sky-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Week
                </button>
                <button
                  onClick={() => setFilterPeriod('month')}
                  className={`px-4 py-2 text-sm font-medium ${filterPeriod === 'month' ? 'bg-sky-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Month
                </button>
                <button
                  onClick={() => setFilterPeriod('year')}
                  className={`px-4 py-2 text-sm font-medium rounded-r-md ${filterPeriod === 'year' ? 'bg-sky-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Year
                </button>
              </div>
            </div>
            
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-blue"></div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card className="bg-white shadow-md">
                    <CardHeader>
                      <CardTitle style={{ color: themeConfig.primary }}>Total Revenue</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>${stats.totalRevenue.toFixed(2)}</div>
                    </CardContent>
                  </Card>

                  <Card className="bg-white shadow-md">
                    <CardHeader>
                      <CardTitle style={{ color: themeConfig.primary }}>Total Orders</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{stats.totalOrders}</div>
                    </CardContent>
                  </Card>

                  <Card className="bg-white shadow-md">
                    <CardHeader>
                      <CardTitle style={{ color: themeConfig.primary }}>Active Products</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{stats.activeProducts}</div>
                    </CardContent>
                  </Card>

                  <Card className="bg-white shadow-md">
                    <CardHeader>
                      <CardTitle style={{ color: themeConfig.primary }}>Active Customers</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{stats.activeCustomers}</div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6">
                  <Card className="bg-white shadow-md">
                    <CardHeader>
                      <CardTitle style={{ color: themeConfig.primary }}>Sales Trend ({filterPeriod})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <LineChart
                        data={stats.salesData || []}
                      />
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-white shadow-md">
                    <CardHeader>
                      <CardTitle style={{ color: themeConfig.primary }}>Order Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <PieChart
                        data={stats.orderStatus || []}
                      />
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>
         
          <TabsContent value="products" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold" style={{ color: themeConfig.primary }}>Inventory Management</h3>
              <div className="inline-flex rounded-md shadow-sm">
                <button
                  onClick={() => setInventoryFilter('all')}
                  className={`px-4 py-2 text-sm font-medium rounded-l-md ${inventoryFilter === 'all' ? 'bg-sky-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  All Products
                </button>
                <button
                  onClick={() => setInventoryFilter('low')}
                  className={`px-4 py-2 text-sm font-medium ${inventoryFilter === 'low' ? 'bg-sky-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Low Stock
                </button>
                <button
                  onClick={() => setInventoryFilter('expiring')}
                  className={`px-4 py-2 text-sm font-medium rounded-r-md ${inventoryFilter === 'expiring' ? 'bg-sky-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Expiring Soon
                </button>
              </div>
            </div>
            
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-blue"></div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <Card className="bg-white shadow-md">
                    <CardHeader>
                      <div className="flex items-center">
                        <ShoppingBagIcon className="h-5 w-5 mr-2" style={{ color: themeConfig.primary }} />
                        <CardTitle style={{ color: themeConfig.primary }}>Top Selling Products</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <BarChart
                        data={Object.entries(filteredTopSellingProducts || {}).map(([product, sales]) => ({
                          name: product,
                          value: sales as number
                        }))}
                      />
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-white shadow-md">
                    <CardHeader>
                      <div className="flex items-center">
                        <ChartBarIcon className="h-5 w-5 mr-2" style={{ color: themeConfig.primary }} />
                        <CardTitle style={{ color: themeConfig.primary }}>Product Categories</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <PieChart
                        data={Object.entries(filteredProductCategories || {}).map(([category, count]) => ({
                          name: category,
                          value: count as number
                        }))}
                      />
                    </CardContent>
                  </Card>
                </div>
                
                <div className="mt-6">
                  <Card className="bg-white shadow-md">
                    <CardHeader>
                      <CardTitle style={{ color: themeConfig.primary }}>
                        {inventoryFilter === 'all' ? 'All Products' : 
                          inventoryFilter === 'low' ? 'Low Stock Products' : 'Expiring Products'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Name</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Product A</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">SKU-001</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">120</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Good</span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2025-08-15</td>
                            </tr>
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Product B</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">SKU-002</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">5</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Low</span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2025-09-20</td>
                            </tr>
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Product C</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">SKU-003</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">35</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">Medium</span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2025-05-30</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="customers" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold" style={{ color: themeConfig.primary }}>Customer Segments</h3>
              <div className="inline-flex rounded-md shadow-sm">
                <button
                  onClick={() => setCustomerSegment('all')}
                  className={`px-4 py-2 text-sm font-medium rounded-l-md ${customerSegment === 'all' ? 'bg-sky-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  All Customers
                </button>
                <button
                  onClick={() => setCustomerSegment('new')}
                  className={`px-4 py-2 text-sm font-medium ${customerSegment === 'new' ? 'bg-sky-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  New
                </button>
                <button
                  onClick={() => setCustomerSegment('returning')}
                  className={`px-4 py-2 text-sm font-medium ${customerSegment === 'returning' ? 'bg-sky-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Returning
                </button>
                <button
                  onClick={() => setCustomerSegment('inactive')}
                  className={`px-4 py-2 text-sm font-medium rounded-r-md ${customerSegment === 'inactive' ? 'bg-sky-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Inactive
                </button>
              </div>
            </div>
            
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-blue"></div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <Card className="bg-white shadow-md">
                    <CardHeader>
                      <div className="flex items-center">
                        <UserGroupIcon className="h-5 w-5 mr-2" style={{ color: themeConfig.primary }} />
                        <CardTitle style={{ color: themeConfig.primary }}>Customer Demographics</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <BarChart
                        data={Object.entries(filteredCustomerData.distribution || {}).map(([parish, count]) => ({
                          name: parish,
                          value: count as number
                        }))}
                      />
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-white shadow-md">
                    <CardHeader>
                      <div className="flex items-center">
                        <ChartBarIcon className="h-5 w-5 mr-2" style={{ color: themeConfig.primary }} />
                        <CardTitle style={{ color: themeConfig.primary }}>Order Time Distribution</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <LineChart
                        data={Object.entries(filteredCustomerData.timeDistribution || {}).map(([time, count]) => ({
                          name: time,
                          value: count as number
                        }))}
                      />
                    </CardContent>
                  </Card>
                </div>
                
                <div className="mt-6">
                  <Card className="bg-white shadow-md">
                    <CardHeader>
                      <CardTitle style={{ color: themeConfig.primary }}>
                        {customerSegment === 'all' ? 'All Customers' : 
                          customerSegment === 'new' ? 'New Customers' : 
                          customerSegment === 'returning' ? 'Returning Customers' : 'Inactive Customers'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spent</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Order</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">John Doe</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">john@example.com</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">12</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">$1,245.00</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2025-05-02</td>
                            </tr>
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Jane Smith</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">jane@example.com</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">5</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">$450.75</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2025-04-15</td>
                            </tr>
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Bob Johnson</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">bob@example.com</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">1</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">$95.25</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2025-05-10</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
        </div>
      </div>
    </div>
  );
}
